import { Component, inject, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { PaginatorState } from 'primeng/paginator';
import { AppPageLayoutComponent } from '@view/shared/templates/page-layout/page-layout.component';
import { ReportLoadsGateway } from '@service/report-loads/report-loads.gateway';
import {
  ReportLoad,
  ReportLoadPage
} from '@model/report-load/report-load.model';
import { AppError } from '@domain-utils/app-error';
import { MESSAGE_CONSTANTS } from '@view/shared/constants/message.const';
import { finalize, map, Observable } from 'rxjs';
import { UserGateway } from '@service/user/user.gateway';
import {
  FilterMode,
  FILTER_MODE
} from '@view/shared/constants/filterMode.const';
import { ReportErrorCardComponent } from '../components/report-error-card/report-error-card.component';
import { ReportFilterBarComponent } from '../components/report-filter-bar/report-filter-bar.component';
import { ReportTableComponent } from '../components/report-table/report-table.component';
import { AppConfirmationService } from '@service/confirmation/app-confirmation.service';
import { AppNotificationService } from '@service/notification/app-notification.service';
import { ReportDeleteSuccessCardComponent } from '../components/report-delete-success-card/report-delete-success-card.component';

@Component({
  selector: 'app-bulk-risk-report',
  imports: [
    RouterLink,
    AppPageLayoutComponent,
    ReportErrorCardComponent,
    ReportFilterBarComponent,
    ReportTableComponent,
    ReportDeleteSuccessCardComponent
  ],
  templateUrl: './bulk-risk-report.component.html',
  styleUrl: './bulk-risk-report.component.scss'
})
export class BulkRiskReportComponent implements OnInit {
  private readonly reportLoadsService = inject(ReportLoadsGateway);
  private readonly userService = inject(UserGateway);
  private readonly confirmationService = inject(AppConfirmationService);
  private readonly notificationService = inject(AppNotificationService);

  filterText = signal('');
  filterMode = signal<FilterMode>(FILTER_MODE.LOAD_ID);
  activeFilterMode = signal<FilterMode>(FILTER_MODE.DNI);
  activeFilterValue = signal('');
  currentPage = signal(0);
  rowsPerPage = signal(5);
  totalRecords = signal(0);
  readonly rowsPerPageOptions = [5, 10, 20];
  isLoading = signal(false);
  isRefreshing = signal(false);
  error = signal<string | null>(null);
  errorTitle = signal('No se pudo cargar el reporte');
  loads = signal<ReportLoad[]>([]);
  deletingLoadId = signal<string | null>(null);
  deletedLoadId = signal<string | null>(null);
  deletedPolicyNumber = signal<string | null>(null);

  ngOnInit(): void {
    this.loadData();
  }

  loadData(isRefresh = false): void {
    this.setLoadingState(isRefresh);
    this.error.set(null);
    this.clearDeleteSuccess();

    this.userService.getUser().subscribe({
      next: (user) => {
        const dni = `${user?.dni ?? ''}`.trim();
        if (!dni) {
          this.errorTitle.set('No se pudo cargar el reporte');
          this.error.set(MESSAGE_CONSTANTS.REPORT_LOAD_ERROR);
          this.clearLoadingState();
          return;
        }
        this.loadByMode(FILTER_MODE.DNI, dni, isRefresh, true);
      },
      error: (err: unknown) => {
        this.handleLoadError(err);
        this.clearLoadingState();
      }
    });
  }

  onFilterChange(value: string): void {
    this.filterText.set(value);
    if (!value.trim() && this.activeFilterMode() !== FILTER_MODE.DNI) {
      this.loadData();
    }
  }

  setFilterMode(mode: FilterMode): void {
    if (this.filterMode() === mode) return;
    this.filterMode.set(mode);
    this.filterText.set('');
  }

  applyFilter(): void {
    const query = this.filterText().trim();
    if (
      !query ||
      (query === this.activeFilterValue() &&
        this.filterMode() === this.activeFilterMode())
    ) {
      return;
    }

    this.clearDeleteSuccess();
    this.loadByMode(this.filterMode(), query, false, true);
  }

  clearError(): void {
    this.error.set(null);
  }

  resetFilters(): void {
    this.filterText.set('');
    this.filterMode.set(FILTER_MODE.LOAD_ID);
    this.loadData();
  }

  refreshTable(): void {
    if (this.isRefreshing()) return;
    this.loadByMode(this.activeFilterMode(), this.activeFilterValue(), true);
  }

  onPageChange(event: PaginatorState): void {
    if (this.activeFilterMode() === FILTER_MODE.LOAD_ID) {
      return;
    }

    const nextPage = event.page ?? 0;
    const nextRows = event.rows ?? this.rowsPerPage();

    this.currentPage.set(nextPage);
    this.rowsPerPage.set(nextRows);

    this.loadByMode(this.activeFilterMode(), this.activeFilterValue());
  }

  async deleteLoad(load: ReportLoad): Promise<void> {
    const loadId = load?.loadId?.trim();

    if (!loadId || this.deletingLoadId()) {
      return;
    }

    const confirmed = await this.confirmationService.confirmDelete(
      'reportLoad',
      loadId
    );

    if (!confirmed) {
      return;
    }

    this.deletingLoadId.set(loadId);
    this.error.set(null);

    this.reportLoadsService
      .deleteLoad(loadId)
      .pipe(finalize(() => this.deletingLoadId.set(null)))
      .subscribe({
        next: () => {
          this.loads.update((current) =>
            current.filter((item) => item.loadId !== loadId)
          );

          this.totalRecords.update((current) => Math.max(current - 1, 0));
          this.deletedLoadId.set(loadId);
          this.deletedPolicyNumber.set(load.policyNumber ?? null);

          this.notificationService.success(
            MESSAGE_CONSTANTS.REPORT_LOAD_DELETE_SUCCESS
          );
        },
        error: (err: unknown) => {
          const message =
            err instanceof AppError
              ? err.codes?.[0]?.message || err.message
              : MESSAGE_CONSTANTS.REPORT_LOAD_DELETE_ERROR;
          this.clearDeleteSuccess();
          this.errorTitle.set('No se pudo eliminar la carga');
          this.error.set(message);
          this.notificationService.error(message);
        }
      });
  }

  clearDeleteSuccess(): void {
    this.deletedLoadId.set(null);
    this.deletedPolicyNumber.set(null);
  }

  private loadByMode(
    mode: FilterMode,
    value: string,
    isRefresh = false,
    resetPage = false
  ): void {
    if (resetPage) {
      this.currentPage.set(0);
    }

    const normalizedValue = value.trim();
    if (!normalizedValue) {
      this.errorTitle.set('No se pudo cargar el reporte');
      this.error.set(MESSAGE_CONSTANTS.REPORT_LOAD_ERROR);
      this.clearLoadingState();
      return;
    }

    const page = this.currentPage();
    const size = this.rowsPerPage();

    const request$ = this.buildLoadRequest(mode, normalizedValue, page, size);

    this.activeFilterMode.set(mode);
    this.activeFilterValue.set(normalizedValue);

    this.executeLoad(request$, isRefresh);
  }

  private buildLoadRequest(
    mode: FilterMode,
    value: string,
    page: number,
    size: number
  ): Observable<{
    loads: ReportLoad[];
    totalRecords: number;
  }> {
    if (mode === FILTER_MODE.POLICY) {
      return this.reportLoadsService
        .getLoadsByPolicy(value, page, size)
        .pipe(map((response) => this.mapPageResponse(response)));
    }

    if (mode === FILTER_MODE.LOAD_ID) {
      const loadIdRequest = value.toLocaleUpperCase();
      return this.reportLoadsService.getLoadById(loadIdRequest).pipe(
        map((load) => {
          const loads = load?.loadId ? [load] : [];
          return {
            loads,
            totalRecords: loads.length
          };
        })
      );
    }

    return this.reportLoadsService
      .getLoadsByDNI(value, page, size)
      .pipe(map((response) => this.mapPageResponse(response)));
  }

  private executeLoad(
    request$: Observable<{ loads: ReportLoad[]; totalRecords: number }>,
    isRefresh: boolean
  ): void {
    this.setLoadingState(isRefresh);
    this.error.set(null);

    request$.pipe(finalize(() => this.clearLoadingState())).subscribe({
      next: (response) => {
        this.loads.set(response.loads);
        this.totalRecords.set(response.totalRecords);
      },
      error: (err: unknown) => {
        this.handleLoadError(err);
      }
    });
  }

  private mapPageResponse(response: ReportLoadPage): {
    loads: ReportLoad[];
    totalRecords: number;
  } {
    return {
      loads: response.items,
      totalRecords: response.totalRecords
    };
  }

  private handleLoadError(err: unknown): void {
    this.errorTitle.set('No se pudo cargar el reporte');
    this.loads.set([]);
    if (err instanceof AppError) {
      const firstMessage = err.codes?.[0]?.message ?? err.message;
      this.error.set(firstMessage || MESSAGE_CONSTANTS.REPORT_LOAD_ERROR);
      return;
    }
    this.error.set(MESSAGE_CONSTANTS.REPORT_LOAD_UNEXPECTED_ERROR);
  }

  private setLoadingState(isRefresh: boolean): void {
    if (isRefresh) {
      this.isRefreshing.set(true);
      return;
    }
    this.isLoading.set(true);
  }

  private clearLoadingState(): void {
    this.isLoading.set(false);
    this.isRefreshing.set(false);
  }
}
